import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BudgetCollaborationService, BudgetEvidence } from '../../../services/budget-collaboration.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { isAttachmentImage } from '../../../shared/utils/attachment-image.util';

/** Espelha o limite validado no backend. */
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

/**
 * Fotos e documentos do orçamento.
 *
 * O upload vai direto para o Blob Storage com SAS; a API só assina a URL e
 * registra os metadados depois.
 */
@Component({
  selector: 'app-budget-evidences',
  templateUrl: './budget-evidences.component.html',
  styleUrls: ['./budget-collaboration.component.css']
})
export class BudgetEvidencesComponent implements OnChanges {
  @Input() budgetId: number | null = null;
  @Input() groupByLine = false;
  @Input() lineLabels: Array<{ id: number; description: string }> = [];
  @Input() lineItems: Array<{ id: number; description: string }> = [];

  evidencias: BudgetEvidence[] = [];
  loading = false;
  uploading = false;

  constructor(
    private collaborationService: BudgetCollaborationService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['budgetId']) {
      this.evidencias = [];
      this.load();
    }
  }

  load(): void {
    if (!this.budgetId) return;

    this.loading = true;
    this.collaborationService.listEvidences(this.budgetId).subscribe({
      next: res => {
        this.evidencias = res ?? [];
        this.loading = false;
      },
      error: err => {
        this.errorService.showErrorMessage(err?.message || 'Falha ao carregar evidências.');
        this.loading = false;
      }
    });
  }

  get fotos(): BudgetEvidence[] {
    return this.evidencias.filter(e => this.isImage(e));
  }

  get documentos(): BudgetEvidence[] {
    return this.evidencias.filter(e => !this.isImage(e));
  }

  get generalEvidencias(): BudgetEvidence[] {
    return this.evidencias.filter(e => !e.linhaId);
  }

  groupedLineEvidences(): Array<{ key: string; title: string; items: BudgetEvidence[] }> {
    if (!this.groupByLine) return [];

    const groups: Array<{ key: string; title: string; items: BudgetEvidence[] }> = [];

    (this.lineLabels || []).forEach((s, i) => {
      const items = this.evidencias.filter(e => e.linhaId === s.id && (e.linhaTipo || 'servico') === 'servico');
      if (items.length) {
        groups.push({ key: `servico-${s.id}`, title: `Serviço ${i + 1} — ${s.description}`, items });
      }
    });

    (this.lineItems || []).forEach((it, i) => {
      const items = this.evidencias.filter(e => e.linhaId === it.id && e.linhaTipo === 'item');
      if (items.length) {
        groups.push({ key: `item-${it.id}`, title: `Peça ${i + 1} — ${it.description}`, items });
      }
    });

    return groups;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Libera o input para permitir reenviar o mesmo arquivo após um erro.
    input.value = '';

    if (!file || !this.budgetId) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.errorService.showWarningMessage('Arquivo excede o limite de 25 MB.');
      return;
    }

    this.uploading = true;
    this.collaborationService.uploadAndRegisterEvidence(this.budgetId, file).subscribe({
      next: nova => {
        this.evidencias = [...this.evidencias, nova];
        this.uploading = false;
        this.errorService.showSuccessMessage('Evidência anexada.');
      },
      error: err => {
        this.errorService.showErrorMessage(err?.message || 'Falha ao enviar o arquivo.');
        this.uploading = false;
      }
    });
  }

  remove(evidencia: BudgetEvidence): void {
    if (!this.budgetId) return;

    this.confirmationService
      .confirm({
        title: 'Remover evidência',
        message: `Remover "${evidencia.fileName}" deste orçamento?`,
        confirmText: 'Remover',
        cancelText: 'Cancelar',
        type: 'danger'
      })
      .subscribe(confirmed => {
        if (!confirmed) return;

        this.collaborationService.deleteEvidence(this.budgetId!, evidencia.id).subscribe({
          next: () => {
            this.evidencias = this.evidencias.filter(e => e.id !== evidencia.id);
            this.errorService.showSuccessMessage('Evidência removida.');
          },
          error: err => this.errorService.showErrorMessage(err?.message || 'Falha ao remover.')
        });
      });
  }

  isImage(e: BudgetEvidence): boolean {
    return isAttachmentImage(e);
  }

  origemLabel(e: BudgetEvidence): string {
    return e.autorOrigem === 'Portal' ? 'Cliente' : 'Equipe';
  }

  formatSize(bytes: number | null | undefined): string {
    if (!bytes || bytes <= 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
